import { useState, useRef, useEffect, useMemo } from "react";
import {
  MotifModal,
  MotifModalBody,
  MotifModalHeader,
  MotifModalFooter,
  MotifButton,
  MotifTheme,
  MotifFormField,
  MotifRadioButton,
  MotifRadioButtonGroup,
  MotifFileUploader,
  MotifFileUploaderItem,
  MotifIcon,
  MotifErrorMessage,
  MotifTextArea,
  MotifIconButton,
  MotifLabel,
  MotifInput,
  MotifSelect,
  MotifOption,
  MotifToast,
} from "@ey-xd/motif-react";
import "./ArtifactsModal.scss";
import {
  RESOURCE_NAME,
  TAG,
  RESOURCE_DESCRIPTION,
  LINK,
  RESOURCE_TYPE,
  VALIDATION_RULES,
  ERROR_INITIAL,
  MAX_FILE_SIZE,
  ALLOWED_EXTENSIONS,
  UPLOAD_DURATION,
  UPDATE_INTERVAL,
  MAX_LENGTH_RESOURCE_NAME,
  MAX_LENGTH_RESOURCE_DESCRIPTION,
  MAX_LENGTH_LINK,
} from "./Constants";
import { useSelector, useDispatch } from "react-redux";
import { fetchSkuConfigData } from "../../store/slices/CTDBSkuConfigSlice";
import { AppDispatch, RootState } from "../../store";
import { createAttachment } from "../../store/slices/CreateAttachmentSlice";
import { IconoirCloudUpload, IconoirTrash } from "@ey-xd/motif-icon";
import {
  isEmpty,
  ltrimString,
  isInputStringValid,
  isValidEmail,
  isValidUrl,
  isValidUrlWithWWW,
  isFileNameContainsDangerousExtensions,
} from "../../utils/Validator";
import {
  EMPTY_FILE_MESSAGE,
  EMPTY_FILE_TITLE,
  WAF_ERROR_MESSAGE,
} from "../../Constant";

interface ArtifactsModalProps {
  show: boolean;
  setShow: (show: boolean) => void;
  editMode: boolean;
  productId: string;
  artifactToEdit?: ArtifactType;
  refetchArtifacts: (message: string) => void;
}
interface ArtifactType {
  ProductArtifactId: string;
  ProductId: string;
  Title: string;
  Description: string;
  URL: string;
  ArtifactTypeCd: string;
  ArtifactCategoryCd: string;
  IsActive: boolean;
  CreatedUser: string;
  UpdatedUser: string;
  FileSize: number | null;
}

const ArtifactsModal = ({
  show,
  setShow,
  productId,
  editMode,
  refetchArtifacts,
  artifactToEdit,
}: ArtifactsModalProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const skuConfigData = useSelector(
    (state: RootState) => state.CTDBSkuConfigSlice.data,
  );

  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [show]);

  const [errorMessage, setErrorMessage] = useState<{
    header: string;
    message: string;
  }>({ header: "", message: "" });
  const [uploadedFile, setUploadedFile] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [editModeFileName, setEditModeFileName] = useState<string>("");
  const [existingFileRemoved, setExistingFileRemoved] =
    useState<boolean>(false);
  const [isArtifactsSaving, setIsArtifactsSaving] = useState<boolean>(false);
  const uploadIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const filteredTagList = useMemo(
    () =>
      skuConfigData?.CommercialStatus?.filter(
        (item: { StatusType: string }) => item.StatusType === "prodartcatg",
      ) || [],
    [skuConfigData],
  );

  const tagList = useMemo(
    () =>
      filteredTagList?.map((tag: { Name: string; StatusCd: string }) => ({
        label: tag.Name,
        value: tag.StatusCd,
      })) || [],
    [filteredTagList],
  );

  // Define initial values and error states
  const initialValues = {
    resourceType: "",
    resourceName: "",
    tag: "",
    description: "",
    link: "",
  };

  // Use ERROR_INITIAL for error state
  const [formErrors, setFormErrors] = useState(ERROR_INITIAL);
  const [formFields, setFormFields] = useState(initialValues);
  useEffect(() => {
    if (editMode && artifactToEdit && tagList.length > 0) {
      setFormFields({
        resourceType:
          artifactToEdit.ArtifactTypeCd === "prodarttype_doc"
            ? "Document"
            : "Link",
        resourceName: artifactToEdit.Title,
        tag: artifactToEdit.ArtifactCategoryCd,
        description: artifactToEdit.Description,
        link: artifactToEdit.URL,
      });
      if (artifactToEdit.ArtifactTypeCd === "prodarttype_doc") {
        setEditModeFileName(getFileNameFromUrl(artifactToEdit.URL));
      }
    }
  }, [editMode, artifactToEdit, tagList]);

  const { resourceType, resourceName, tag, description, link } = formFields;
  // Check if tag value matches available options
  const tagMatches = tagList.some((t: any) => t.value === tag);

  const getFileNameFromUrl = (url: string) => {
    if (!url) return "";
    return url.split("/").pop() || "";
  };

  const validateFormFields = (
    name?: string,
    value?: string,
    setErrorState?: typeof setFormErrors,
  ) => {
    const errors = { ...formErrors };
    let hasErrors = false;

    const isFieldEmpty = (fieldValue?: string) => {
      const normalizedValue = ltrimString(fieldValue ?? "");
      return isEmpty(normalizedValue) || normalizedValue.length === 0;
    };

    if (name && value !== undefined && setErrorState) {
      if (name === "resourceType") {
        if (value === "Document") {
          setErrorState((prev) => ({
            ...prev,
            link: { isError: false, message: "" },
          }));
        } else if (value === "Link") {
          setErrorState((prev) => ({
            ...prev,
            description: { isError: false, message: "" },
          }));
        }
      }

      if (name === "resourceType") {
        if (VALIDATION_RULES.resourceType.required && !value) {
          errors.resourceType = {
            isError: true,
            message: `Please select a ${RESOURCE_TYPE}.`,
          };
          hasErrors = true;
        } else {
          errors.resourceType = { isError: false, message: "" };
        }
      }

      if (name === "resourceName") {
        if (VALIDATION_RULES.resourceName.required && isFieldEmpty(value)) {
          errors.resourceName = {
            isError: true,
            message: `Please provide a ${RESOURCE_NAME.toLowerCase()}.`,
          };
          hasErrors = true;
        } else if (value.length > VALIDATION_RULES.resourceName.maxLength) {
          errors.resourceName = {
            isError: true,
            message: `${RESOURCE_NAME} cannot exceed ${VALIDATION_RULES.resourceName.maxLength} characters.`,
          };
          hasErrors = true;
        } else {
          errors.resourceName = { isError: false, message: "" };
        }
      } else if (name === "tag") {
        if (VALIDATION_RULES.tag.required && !value) {
          errors.tag = {
            isError: true,
            message: `Please select a ${TAG.toLowerCase()} for your resource.`,
          };
          hasErrors = true;
        } else {
          errors.tag = { isError: false, message: "" };
        }
      } else if (name === "description") {
        if (VALIDATION_RULES.description.required && isFieldEmpty(value)) {
          errors.description = {
            isError: true,
            message: `Please provide a ${RESOURCE_DESCRIPTION.toLowerCase()}.`,
          };
          hasErrors = true;
        } else if (value.length > VALIDATION_RULES.description.maxLength) {
          errors.description = {
            isError: true,
            message: `${RESOURCE_DESCRIPTION} cannot exceed ${VALIDATION_RULES.description.maxLength} characters.`,
          };
          hasErrors = true;
        } else {
          errors.description = { isError: false, message: "" };
        }
      } else if (name === "link") {
        if (VALIDATION_RULES.link.required && !value) {
          errors.link = {
            isError: true,
            message: `Please provide a ${LINK.toLowerCase()}.`,
          };
          hasErrors = true;
        } else if (
          value &&
          (isValidEmail(value) ||
            !isValidUrl(value) ||
            !isValidUrlWithWWW(value))
        ) {
          errors.link = {
            isError: true,
            message: `Invalid link. Please enter a valid URL starting with http://, https://, or www.`,
          };
          hasErrors = true;
        } else if (value.length > VALIDATION_RULES.link.maxLength) {
          errors.link = {
            isError: true,
            message: `${LINK} cannot exceed ${VALIDATION_RULES.link.maxLength} characters.`,
          };
          hasErrors = true;
        } else {
          errors.link = { isError: false, message: "" };
        }
      }

      setErrorState(errors);
      return !hasErrors;
    }

    // Validate all fields using current state values (from handleSave)
    //validate resourceType
    if (!resourceType) {
      errors.resourceType = {
        isError: true,
        message: `Please provide a ${RESOURCE_TYPE.toLowerCase()}.`,
      };
      hasErrors = true;
    } else {
      errors.resourceType = { isError: false, message: "" };
    }
    // Validate resourceName
    if (isFieldEmpty(resourceName)) {
      errors.resourceName = {
        isError: true,
        message: `Please provide a ${RESOURCE_NAME.toLowerCase()}.`,
      };
      hasErrors = true;
    } else if (resourceName.length > VALIDATION_RULES.resourceName.maxLength) {
      errors.resourceName = {
        isError: true,
        message: `${RESOURCE_NAME} cannot exceed ${VALIDATION_RULES.resourceName.maxLength} characters.`,
      };
      hasErrors = true;
    } else {
      errors.resourceName = { isError: false, message: "" };
    }

    // Validate tag
    if (!tag) {
      errors.tag = {
        isError: true,
        message: `Please select a ${TAG.toLowerCase()} for your resource.`,
      };
      hasErrors = true;
    } else {
      errors.tag = { isError: false, message: "" };
    }

    if (isFieldEmpty(description)) {
      errors.description = {
        isError: true,
        message: `Please provide a ${RESOURCE_DESCRIPTION.toLowerCase()}.`,
      };
      hasErrors = true;
    } else if (description.length > VALIDATION_RULES.description.maxLength) {
      errors.description = {
        isError: true,
        message: `${RESOURCE_DESCRIPTION} cannot exceed ${VALIDATION_RULES.description.maxLength} characters.`,
      };
      hasErrors = true;
    } else {
      errors.description = { isError: false, message: "" };
    }

    // Validate description for Document type
    if (resourceType === "Document") {
      if (
        uploadedFile.length === 0 &&
        !(editMode && editModeFileName && !existingFileRemoved)
      ) {
        setFileError("Resource File is required.");
        hasErrors = true;
      } else if (fileError) {
        hasErrors = true;
      }
    }

    // Validate link for Link type
    if (resourceType === "Link") {
      if (isFieldEmpty(link)) {
        errors.link = {
          isError: true,
          message: `Please provide a ${LINK.toLowerCase()}.`,
        };
        hasErrors = true;
      } else if (
        isValidEmail(link) ||
        !isValidUrl(link) ||
        !isValidUrlWithWWW(link)
      ) {
        errors.link = {
          isError: true,
          message: `Invalid link. Please enter a valid URL starting with http://, https://, or www.`,
        };
        hasErrors = true;
      } else if (link.length > VALIDATION_RULES.link.maxLength) {
        errors.link = {
          isError: true,
          message: `${LINK} cannot exceed ${VALIDATION_RULES.link.maxLength} characters.`,
        };
        hasErrors = true;
      } else {
        errors.link = { isError: false, message: "" };
      }
    }

    setFormErrors(errors);

    if (hasErrors) {
      setErrorMessage({
        header: "Some information is missing",
        message: "Please review the form and provide the required details.",
      });
    }
    return !hasErrors;
  };

  const simulateFileUpload = (file: File) => {
    // Clear any existing interval
    if (uploadIntervalRef.current) {
      clearInterval(uploadIntervalRef.current);
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadedFile([file]);

    const steps = UPLOAD_DURATION / UPDATE_INTERVAL;
    const incrementPerStep = 100 / steps;
    let currentProgress = 0;

    uploadIntervalRef.current = setInterval(() => {
      currentProgress += incrementPerStep;

      if (currentProgress >= 100) {
        setUploadProgress(100);
        setIsUploading(false);
        if (uploadIntervalRef.current) {
          clearInterval(uploadIntervalRef.current);
          uploadIntervalRef.current = null;
        }
      } else {
        setUploadProgress(Math.round(currentProgress));
      }
    }, UPDATE_INTERVAL);
  };

  const processFiles = (files: any) => {
    if (!files) return;

    // Handle direct File object
    if (files instanceof File) {
      uploadFile([files]);
      return;
    }

    // Handle FileList or array-like object
    if (files.length !== undefined && files.length > 0) {
      // Check if first element is a File
      if (files[0] instanceof File) {
        uploadFile(files);
      } else if (files[0]?.file instanceof File) {
        // Handle wrapped file objects
        uploadFile([files[0].file]);
      }
      return;
    }

    // Handle event object with dataTransfer or target.files
    if (files.dataTransfer?.files || files.target?.files) {
      uploadFile(files.dataTransfer?.files || files.target.files);
      return;
    }

    // Handle object with file property
    if (files.file instanceof File) {
      uploadFile([files.file]);
      return;
    }
  };

  const uploadFile = (files: FileList | File[]) => {
    if (!files?.length) return;
    setErrorMessage({ header: "", message: "" });
    setFileError(null);

    if (uploadedFile.length >= 1) {
      setFileError(
        "A file is already uploaded. Please remove it first before uploading a new one.",
      );
      return;
    }

    const file = files[0] as File;
    const fileExtension = file.name.split(".").pop()?.toLowerCase();

    // Validate file extension
    if (!fileExtension || !ALLOWED_EXTENSIONS.includes(fileExtension as any)) {
      setFileError(
        `The file ${file.name} is not a supported format. Please upload a PDF, PPT, PPTX, DOC, or DOCX file.`,
      );
      setUploadedFile([]);
      return;
    }
    //validate double extension
    const isValidFile = isFileNameContainsDangerousExtensions(file.name);
    if (!isValidFile) {
      setErrorMessage({
        header: "Invalid file type",
        message: `Your file ${file.name} contains an unsupported extensions. Allowed file types: PNG, PDF, DOC, DOCX, MSG. Please remove any restricted extensions (e.g. PHP, ASP, EXE) and try again`,
      });
      setUploadedFile([]);
      scrollToTop();
      return;
    }

    // Validate empty file
    if (file.size === 0) {
      setUploadedFile([file]);
      setFileError(EMPTY_FILE_MESSAGE);
      setErrorMessage({
        header: EMPTY_FILE_TITLE,
        message: EMPTY_FILE_MESSAGE,
      });
      scrollToTop();
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setFileError(
        `The size of your file ${file.name} is bigger than 15 MB. Please select and upload file according to the size limitations.`,
      );
      setUploadedFile([]);
      return;
    }

    setFileError(null);
    simulateFileUpload(file);
  };

  const handleFileRemove = () => {
    if (editMode && editModeFileName && !existingFileRemoved) {
      setEditModeFileName("");
      setExistingFileRemoved(true);
      return;
    }
    if (uploadIntervalRef.current) {
      clearInterval(uploadIntervalRef.current);
      uploadIntervalRef.current = null;
    }
    setUploadedFile([]);
    setFileError(null);
    setUploadProgress(0);
    setIsUploading(false);
  };

  const handleChange = (event: { target: { name: string; value: string } }) => {
    const name = event.target.name;
    let value = event.target.value;

    // validating the input text for name and description
    if (name !== "link" && !isInputStringValid(event.target.value)) {
      return;
    }

    if (name === "resourceType") {
      setFormFields((prev) => ({
        ...prev,
        link: "",
      }));
      setUploadedFile([]);
    }

    if (name === "resourceName" && value.length > MAX_LENGTH_RESOURCE_NAME) {
      value = value.slice(0, MAX_LENGTH_RESOURCE_NAME);
    }

    if (
      name === "description" &&
      value.length > MAX_LENGTH_RESOURCE_DESCRIPTION
    ) {
      value = value.slice(0, MAX_LENGTH_RESOURCE_DESCRIPTION);
    }

    if (name === "link" && value.length > MAX_LENGTH_LINK) {
      value = value.slice(0, MAX_LENGTH_LINK);
    }

    setFormFields((prev) => ({
      ...prev,
      [name]: value,
    }));

    validateFormFields(name, value, setFormErrors);
  };

  const closeArtifactsModal = () => {
    if (uploadIntervalRef.current) {
      clearInterval(uploadIntervalRef.current);
      uploadIntervalRef.current = null;
    }
    setShow(false);
    setFormFields(initialValues); // Reset formFields to initialValues
    setErrorMessage({ header: "", message: "" });
    setUploadedFile([]);
    setFileError(null);
    setUploadProgress(0);
    setIsUploading(false);
    setExistingFileRemoved(false);
  };

  const scrollToTop = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({
        block: "start",
      });
    }
  };

  const handleSave = async () => {
    setErrorMessage({ header: "", message: "" });
    if (!validateFormFields()) {
      scrollToTop();
      return;
    }

    const attachmentData = {
      Action: editMode ? "Update" : "Create",
      ProductId: productId, // Dynamic ProductId from props
      ResourceType:
        resourceType === "Document" ? "prodarttype_doc" : "prodarttype_link",
      Tag: tag, // Dynamic tag value
      ResourceName: resourceName.trim(), // Dynamic resource name
      ResourceDescription: description.trim() || "", // Dynamic description
      ...(editMode
        ? { ProductArtifactId: artifactToEdit?.ProductArtifactId }
        : {}),
      ...(resourceType === "Document"
        ? {
            ResourceFileContent: uploadedFile[0], // File object for multipart upload
          }
        : { ResourceLink: link.trim() }), // Dynamic link for Link type
    };

    try {
      setIsArtifactsSaving(true);
      const response = await dispatch(createAttachment(attachmentData));
      if (response.payload.Status === "success") {
        setIsArtifactsSaving(false);
        closeArtifactsModal();
        refetchArtifacts(
          editMode
            ? "Artifact has been updated successfully."
            : "Artifact has been created successfully.",
        );
      } else {
        const payload = response.payload as
          | { status?: number; message?: string }
          | undefined;
        const isForbidden = payload?.status === 403;
        const isBadRequest = payload?.status === 400;
        setIsArtifactsSaving(false);
        setErrorMessage({
          header:
            isForbidden && payload?.message === WAF_ERROR_MESSAGE
              ? "Can’t save your changes"
              : payload?.message?.startsWith("File name")
                ? "Duplicate file detected"
                : payload?.message?.startsWith("Artifact limit")
                  ? "Oops! Maximum artifact limit reached."
                  : "Oops! We're having a little trouble on our end.",
          message:
            isBadRequest && payload?.message
              ? payload.message
              : isForbidden && payload?.message === WAF_ERROR_MESSAGE
                ? "This form includes some special characters we don’t support yet. Try removing them and save again. If it still doesn’t work, contact the Helpdesk."
                : payload?.message
                  ? payload.message.startsWith("File name")
                    ? `This document has already been uploaded: ${uploadedFile[0].name}.`
                    : payload.message.startsWith("Artifact limit")
                      ? payload.message
                      : "Please try saving again in few minutes."
                  : "Please try saving again in few minutes.",
        });
        scrollToTop();
      }
    } catch (error) {
      setIsArtifactsSaving(false);
      setErrorMessage({
        header: "Oops! We're having a little trouble on our end.",
        message:
          error instanceof Error
            ? error.message
            : (typeof error === "string" && error) ||
              "Failed to save artifact.",
      });
      scrollToTop();
    }
  };

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (uploadIntervalRef.current) {
        clearInterval(uploadIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (productId) {
      dispatch(fetchSkuConfigData());
    }
  }, [dispatch, productId]);

  const closeToastHandler = () => {
    setErrorMessage({ header: "", message: "" });
  };

  return (
    <div>
      <MotifTheme variant={"light"}></MotifTheme>
      <MotifModal
        onClose={closeArtifactsModal}
        show={show}
        size="md"
        className="artifacts-modal"
        focusTrapOptions={{
          tabbableOptions: {
            displayCheck: "none",
          },
        }}
      >
        <MotifModalHeader
          closeButtonProps={{
            "aria-label": "Close Artifacts Modal",
            title: "Close Artifacts Modal",
          }}
        >
          <span>{editMode ? "Edit Resource" : "Add Resource"}</span>
        </MotifModalHeader>
        <MotifModalBody className="artifacts-modal-body">
          <div ref={scrollRef}>
            <div className="motif-row error-message-div">
              <MotifToast
                variant="info"
                supportingText={
                  "Some fields don’t support special characters.\nTo avoid saving issues, please use letters and numbers only.\nAvoid characters such as < > / \\ | ' \" , ; -- /* */ # ( ) { } [ ]."
                }
              >
                <div>Before you start</div>
              </MotifToast>
            </div>
            {errorMessage.message !== "" && (
              <div className="motif-row error-message-div">
                <MotifToast
                  onClose={closeToastHandler}
                  variant="error"
                  supportingText={errorMessage.message}
                >
                  <div>{errorMessage.header}</div>
                </MotifToast>
              </div>
            )}
          </div>
          <div className="form-group">
            <MotifFormField>
              <MotifLabel>
                {RESOURCE_TYPE}
                <span className="required">*</span>
              </MotifLabel>
              <MotifRadioButtonGroup
                className={
                  formErrors.resourceType.isError ? "motif-invalid" : ""
                }
              >
                <MotifRadioButton
                  value="Document"
                  checked={resourceType === "Document"}
                  name="resourceType"
                  id="resourceTypeDocument"
                  onChange={handleChange}
                  disabled={editMode}
                >
                  Document
                </MotifRadioButton>
                <MotifRadioButton
                  value="Link"
                  checked={resourceType === "Link"}
                  name="resourceType"
                  id="resourceTypeLink"
                  onChange={handleChange}
                  disabled={editMode}
                >
                  Link
                </MotifRadioButton>
              </MotifRadioButtonGroup>
              {formErrors.resourceType.message && (
                <MotifErrorMessage
                  aria-live="assertive"
                  role="alert"
                  id="resourceTypeError"
                >
                  {formErrors.resourceType.message}
                </MotifErrorMessage>
              )}
            </MotifFormField>
          </div>

          <div className="form-group">
            <MotifFormField>
              <MotifLabel>
                {RESOURCE_NAME}
                <span className="required">*</span>
              </MotifLabel>

              <MotifInput
                ariaLabel=""
                label=""
                onChange={handleChange}
                name="resourceName"
                value={resourceName}
                placeholder="Enter resource name"
                hideClearButton={true}
              />
              {formErrors.resourceName.message ? (
                <MotifErrorMessage
                  aria-live="assertive"
                  role="alert"
                  id="resourceNameError"
                >
                  {formErrors.resourceName.message}
                </MotifErrorMessage>
              ) : (
                <div className="description-helper-text">
                  <span>
                    Resource name has a limit of {MAX_LENGTH_RESOURCE_NAME}{" "}
                    characters.
                  </span>
                  <span>
                    {resourceName.length}/{MAX_LENGTH_RESOURCE_NAME}
                  </span>
                </div>
              )}
            </MotifFormField>
          </div>

          <div className="form-group">
            <MotifFormField>
              <MotifLabel>
                {TAG}
                <span className="required">*</span>
              </MotifLabel>

              <MotifSelect
                key={tagMatches ? `tag-matched-${tag}` : `tag-pending-${tag}`}
                onChange={(selectedOption: any) =>
                  handleChange({
                    target: {
                      name: "tag",
                      value: selectedOption,
                    },
                  })
                }
                searchPlaceholder="Search tags"
                multiple={false}
                placeholder="Select a tag"
                value={tag}
              >
                {tagList.map((option) => (
                  <MotifOption
                    isSelected={option.value === tag}
                    key={option.value as string}
                    value={option.value}
                  >
                    {option.label}
                  </MotifOption>
                ))}
              </MotifSelect>
              {formErrors.tag.message && (
                <MotifErrorMessage
                  aria-live="assertive"
                  role="alert"
                  id="tagError"
                >
                  {formErrors.tag.message}
                </MotifErrorMessage>
              )}
            </MotifFormField>
          </div>

          <div className="form-group">
            <MotifFormField>
              <MotifLabel>
                {RESOURCE_DESCRIPTION}
                <span className="required">*</span>
              </MotifLabel>
              <MotifTextArea
                ariaLabel=""
                label=""
                onChange={handleChange}
                name="description"
                value={description}
                placeholder="Enter a description"
                hideClearButton={true}
              />
              {formErrors.description.message ? (
                <MotifErrorMessage
                  aria-live="assertive"
                  role="alert"
                  id="descriptionError"
                >
                  {formErrors.description.message}
                </MotifErrorMessage>
              ) : (
                <div className="description-helper-text">
                  <span> Describe the resource in a few words</span>
                  <span>
                    {description.length}/{MAX_LENGTH_RESOURCE_DESCRIPTION}
                  </span>
                </div>
              )}
            </MotifFormField>
          </div>

          {resourceType === "Document" && (
            <div className="form-group">
              <MotifFormField>
                <MotifLabel>
                  Resource File<span className="required">*</span>
                </MotifLabel>
                {editMode && editModeFileName && !existingFileRemoved ? (
                  <MotifFileUploaderItem
                    key={editModeFileName}
                    aria-label="Existing uploaded file"
                    fileIcon={<MotifIcon src={IconoirCloudUpload} />}
                    fileName={editModeFileName}
                    fileSize={artifactToEdit?.FileSize || 0}
                    percentUploaded={100}
                    showProgress={false}
                    uploaded={true}
                    uploadedText=""
                    className="artifacts-file-item"
                    customButton={
                      <MotifIconButton
                        aria-label="Remove file"
                        size="small"
                        type="button"
                        onClick={handleFileRemove}
                      >
                        <MotifIcon iconFunction={IconoirTrash} />
                      </MotifIconButton>
                    }
                    onRemove={handleFileRemove}
                  />
                ) : (
                  <>
                    <MotifFileUploader
                      label="Click to upload or drag and drop"
                      labelDescription="PDF, PPT, PPTX, DOC or DOCX (max. 15MB)"
                      onDrop={processFiles}
                      onChange={processFiles}
                      maxFiles={1}
                      className={
                        `${uploadedFile.length > 0 ? "hide-uploader" : ""}` +
                        `${fileError ? " motif-invalid" : ""}`
                      }
                    >
                      {uploadedFile.length > 0 &&
                        uploadedFile.map((file) => (
                          <MotifFileUploaderItem
                            key={file.name}
                            aria-label="Item of uploaded file list"
                            fileIcon={<MotifIcon src={IconoirCloudUpload} />}
                            fileName={file.name}
                            fileSize={file.size}
                            percentUploaded={uploadProgress}
                            showProgress={
                              uploadProgress > 0 && uploadProgress < 100
                            }
                            uploaded={uploadProgress === 100}
                            uploadedText=""
                            removable={!isUploading}
                            onRemove={handleFileRemove}
                          />
                        ))}
                    </MotifFileUploader>
                    {fileError && fileError !== EMPTY_FILE_MESSAGE && (
                      <MotifErrorMessage>{fileError}</MotifErrorMessage>
                    )}
                  </>
                )}
              </MotifFormField>
            </div>
          )}

          {resourceType === "Link" && (
            <div className="form-group">
              <MotifFormField>
                <MotifLabel>
                  {LINK}
                  <span className="required">*</span>
                </MotifLabel>
                <MotifInput
                  ariaLabel=""
                  label=""
                  onChange={handleChange}
                  name="link"
                  value={link}
                  placeholder="Enter a link"
                  hideClearButton={true}
                />
                {formErrors.link.message && (
                  <MotifErrorMessage
                    aria-live="assertive"
                    role="alert"
                    id="linkError"
                  >
                    {formErrors.link.message}
                  </MotifErrorMessage>
                )}
              </MotifFormField>
            </div>
          )}
        </MotifModalBody>
        <MotifModalFooter>
          <MotifButton onClick={closeArtifactsModal} variant="secondary">
            Cancel
          </MotifButton>
          <MotifButton
            onClick={handleSave}
            disabled={isUploading || isArtifactsSaving}
          >
            Save
          </MotifButton>
        </MotifModalFooter>
      </MotifModal>
    </div>
  );
};

export default ArtifactsModal;
